import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';

const MONTHS = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
const DAYS = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
const DATE = 'YYYY-MM-DD';

export default function DateCalendar({ value, withTime, min, max, anchor, label, onSelect, onClose }) {
  const lower = min ? min.slice(0, 10) : '0001-01-01';
  const upper = max ? max.slice(0, 10) : '9999-12-31';
  const clamp = day => {
    if (day < lower) return lower;
    return day > upper ? upper : day;
  };
  const initial = clamp(value ? value.slice(0, 10) : moment().format(DATE));
  const [focused, setFocused] = useState(initial);
  const [month, setMonth] = useState(initial.slice(0, 7));
  const [time, setTime] = useState(value.slice(11, 16) || '00:00');
  const [position, setPosition] = useState({ left: 8, top: 8 });
  const popup = useRef(null);
  const start = moment(`${month}-01`, DATE, true);
  const offset = (start.day() + 6) % 7;
  const count = Math.ceil((offset + start.daysInMonth()) / 7) * 7;
  const rows = Array.from({ length: count / 7 }, (_, week) => Array.from({ length: 7 }, (unused, weekday) => {
    const day = week * 7 + weekday - offset + 1;
    return day > 0 && day <= start.daysInMonth() ? start.clone().date(day).format(DATE) : null;
  }));

  useLayoutEffect(() => {
    const reposition = () => {
      const target = anchor.current.getBoundingClientRect();
      const rect = popup.current.getBoundingClientRect();
      const top = target.bottom + rect.height + 8 <= window.innerHeight ? target.bottom + 4 : target.top - rect.height - 4;
      setPosition({ left: Math.max(8, Math.min(target.right - rect.width, window.innerWidth - rect.width - 8)), top: Math.max(8, top) });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [anchor, month]);

  useEffect(() => {
    const button = popup.current.querySelector(`[data-date="${focused}"]`);
    if (button) button.focus();
  }, [focused]);

  useEffect(() => {
    const element = popup.current;
    const escape = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose(true);
      }
    };
    const outside = event => {
      if (!popup.current.contains(event.target) && !anchor.current.contains(event.target)) onClose(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    element.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      element.removeEventListener('keydown', escape);
    };
  }, [anchor, onClose]);

  const select = day => {
    let next = withTime ? `${day}T${time || '00:00'}` : day;
    if (min && next < min) next = min;
    if (max && next > max) next = max;
    onSelect(next);
    onClose(true);
  };
  const navigate = amount => {
    const next = clamp(moment(focused, DATE, true).add(amount, 'months').format(DATE));
    setMonth(next.slice(0, 7));
    setFocused(next);
  };
  const moveFocus = (event, day) => {
    const next = moment(day, DATE, true);
    switch (event.key) {
      case 'ArrowLeft': next.subtract(1, 'day'); break;
      case 'ArrowRight': next.add(1, 'day'); break;
      case 'ArrowUp': next.subtract(7, 'days'); break;
      case 'ArrowDown': next.add(7, 'days'); break;
      case 'Home': next.subtract((next.day() + 6) % 7, 'days'); break;
      case 'End': next.add(6 - ((next.day() + 6) % 7), 'days'); break;
      case 'PageUp': next.subtract(event.shiftKey ? 12 : 1, 'months'); break;
      case 'PageDown': next.add(event.shiftKey ? 12 : 1, 'months'); break;
      default: return;
    }
    event.preventDefault();
    const nextDay = clamp(next.format(DATE));
    setMonth(nextDay.slice(0, 7));
    setFocused(nextDay);
  };

  const calendar = (
    <div
      ref={popup}
      role="dialog"
      aria-label={`Kalendarz: ${label}`}
      className="polish-calendar"
      style={position}
      lang="pl"
      onBlur={event => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget) && event.relatedTarget !== anchor.current) onClose(false);
      }}
    >
      <div className="polish-calendar-heading">
        <button type="button" aria-label="Poprzedni rok" disabled={start.clone().subtract(12, 'months').endOf('month').format(DATE) < lower} onClick={() => navigate(-12)}>«</button>
        <button type="button" aria-label="Poprzedni miesiąc" disabled={start.clone().subtract(1, 'month').endOf('month').format(DATE) < lower} onClick={() => navigate(-1)}>‹</button>
        <strong aria-live="polite">{MONTHS[start.month()]} {start.year()}</strong>
        <button type="button" aria-label="Następny miesiąc" disabled={start.clone().add(1, 'month').format(DATE) > upper} onClick={() => navigate(1)}>›</button>
        <button type="button" aria-label="Następny rok" disabled={start.clone().add(12, 'months').format(DATE) > upper} onClick={() => navigate(12)}>»</button>
      </div>
      <table aria-label="Dni miesiąca">
        <thead><tr>{DAYS.map(day => <th key={day} scope="col">{day}</th>)}</tr></thead>
        <tbody>{rows.map(week => (
          <tr key={week.find(day => day)}>
            {week.map((day, weekday) => (
              <td key={day || `empty-${weekday}`}>
                {day && (
                <button
                  type="button"
                  data-date={day}
                  aria-label={moment(day, DATE, true).format('DD.MM.YYYY')}
                  aria-pressed={day === value.slice(0, 10)}
                  aria-current={day === moment().format(DATE) ? 'date' : undefined}
                  disabled={day < lower || day > upper}
                  tabIndex={day === focused || (focused.slice(0, 7) !== month && day === clamp(start.format(DATE))) ? 0 : -1}
                  onKeyDown={event => moveFocus(event, day)}
                  onClick={() => select(day)}
                >{Number(day.slice(8))}
                </button>
                )}
              </td>
            ))}
          </tr>
        ))}
        </tbody>
      </table>
      {withTime && (
      <div className="polish-calendar-time">
        <span>Godzina</span>
        <input type="time" aria-label="Godzina" value={time} onChange={event => setTime(event.target.value)} />
        <button type="button" onClick={() => select(focused)}>Zastosuj</button>
      </div>
      )}
      <div className="polish-calendar-actions">
        <button type="button" onClick={() => { onSelect(''); onClose(true); }}>Wyczyść</button>
        <button type="button" onClick={() => onClose(true)}>Zamknij</button>
      </div>
    </div>
  );
  return createPortal(calendar, anchor.current.closest('.modal') || document.body);
}

DateCalendar.propTypes = {
  value: PropTypes.string.isRequired,
  withTime: PropTypes.bool.isRequired,
  min: PropTypes.string,
  max: PropTypes.string,
  anchor: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
DateCalendar.defaultProps = { min: undefined, max: undefined };
